import {
    AvailableTimeSuggestionData,
    AvailableTimeSuggestionDialogBackClickedData,
    AvailableTimeSuggestionDialogBookingFormOpenedData,
    AvailableTimeSuggestionDialogFilterChangeData,
    AvailableTimeSuggestionDialogLocationDayData,
    AvailableTimeSuggestionDialogOpenedClosedData,
    BaseInboxEntryData,
    BlockScheduleBatchFormData,
    BlockScheduleCreatedData,
    BlockScheduleUpdatedData,
    CommandPaletteOpenedData,
    CommandPaletteUsedData,
    CommentEditedData,
    CommentMessageCreatedData,
    CommentReactionData,
    CommentThreadCreatedData,
    CommentThreadMutatedData,
    DepartmentSelectedData,
    DrawerData,
    DrawerTabSelectedData,
    HospitalConfigurationPractitionerEditedData,
    HospitalConfigurationPractitionerOrderSavedData,
    HospitalConfigurationPractitionerSavedData,
    HospitalConfigurationRoomEditedData,
    HospitalConfigurationRoomSavedData,
    InboxEntryNavigateToSourceData,
    InboxOpenedData,
    LayoutOptionChangedData,
    LazyLoadErrorFallbackShownData,
    LocationServiceOverrideCreatedData,
    LocationServiceOverrideEditedData,
    NationalIdData,
    ORDayClickedData,
    ORDayCommentData,
    ORDayData,
    ORScheduleFilterChangeData,
    PatientCountHoverCardViewedData,
    PatientsSidepanelData,
    PatientSuggestionClickedData,
    PatientSuggestionDialogBackClickedData,
    PatientSuggestionDialogBookingFormData,
    PatientSuggestionDialogOpenedClosedData,
    PatientSuggestionDialogPreviewSurgeryRemovedData,
    PatientSuggestionDialogUnschedulingFormData,
    PatientSuggestionFilterChangeData,
    PlanningNoteHistoryOpenedData,
    PlanningNoteMigrationData,
    PlanningNoteSavedData,
    PlanningPeriodAddedData,
    PlanningPeriodDescriptionData,
    PlanningPeriodDescriptionRemovedData,
    PlanningPeriodEditedData,
    PlanningPeriodPublicationData,
    ReleaseNoteData,
    RetroORData,
    RetroPlanningPeriodData,
    SpecialtiesSelectedData,
    StaffingModelRunData,
    StaffingTableSettingChangedData,
    StaffingWishData,
    SurgeonActivityData,
    SurgeonLocationData,
    SurgeryBookedData,
    SurgeryBookingFailedData,
    SurgeryMetadataSavedData,
    SurgeryProgramModelData,
    SurgeryProgramModelRunData,
    SurgeryUnscheduledData,
    ViewOptionProfleData,
    VisibilityOptionToggledData,
} from './helpers/dataTypes'

/**
 * User events that are tracked in the application.
 * We follow an object-action naming convention (prefixed with page when necessary).
 */

export type UserEvent =
    // Across pages
    | ['VERSION_UPDATE_TOAST_SHOWN']
    | ['VERSION_UPDATE_TOAST_ACCEPTED']
    | ['VERSION_UPDATE_TOAST_DISMISSED']
    | ['LAZY_LOAD_ERROR_FALLBACK_SHOWN', LazyLoadErrorFallbackShownData]
    | ['OR-DAY_BLOCK_SCHEDULE_ADDED', BlockScheduleCreatedData]
    | ['OR-DAY_BLOCK_SCHEDULE_UPDATED', BlockScheduleUpdatedData]
    | ['OR-DAY_BLOCK_SCHEDULE_REMOVED', BlockScheduleCreatedData]
    | ['LOCATION_SERVICE_OVERRIDE_ADDED', LocationServiceOverrideCreatedData]
    | ['LOCATION_SERVICE_OVERRIDE_UPDATED', LocationServiceOverrideEditedData]
    | ['LOCATION_SERVICE_OVERRIDE_REMOVED', LocationServiceOverrideCreatedData]
    | ['LOCATION_SERVICE_OVERRIDE_FORM_OPENED', ORDayData]
    | ['LOCATION_SERVICE_OVERRIDE_FORM_CLOSED', ORDayData]
    | ['SURGERY_METADATA_SAVED', SurgeryMetadataSavedData]
    | ['NATIONAL_ID_COPIED', NationalIdData]
    | ['DEPARTMENT_SELECTED', DepartmentSelectedData]
    | ['SPECIALTIES_SELECTED', SpecialtiesSelectedData]
    | ['SURGERY_BOOKED', SurgeryBookedData]
    | ['SURGERY_BOOKING_FAILED', SurgeryBookingFailedData]
    | ['SURGERY_UNSCHEDULED', SurgeryUnscheduledData]
    | ['DRAWER_TAB_SELECTED', DrawerTabSelectedData]
    | ['OR-DAY_CLICKED', ORDayClickedData]
    | ['OR-DAY_COMMENT_ADDED', ORDayCommentData]
    | ['OR-DAY_COMMENT_DELETED', ORDayCommentData]
    | ['COMMENT_THREAD_CREATED', CommentThreadCreatedData]
    | ['COMMENT_MESSAGE_CREATED', CommentMessageCreatedData]
    | ['COMMENT_REACTION_ADDED', CommentReactionData]
    | ['COMMENT_REACTION_REMOVED', CommentReactionData]
    | ['COMMENT_THREAD_RESOLVED', CommentThreadMutatedData]
    | ['COMMENT_THREAD_UNRESOLVED', CommentThreadMutatedData]
    | ['COMMENT_THREAD_ARCHIVED', CommentThreadMutatedData]
    | ['COMMENT_THREAD_PINNED', CommentThreadMutatedData]
    | ['COMMENT_THREAD_UNPINNED', CommentThreadMutatedData]
    | ['COMMENT_CONTENT_EDITED', CommentEditedData]
    | ['INBOX_ENTRY_MARKED_AS_READ', BaseInboxEntryData]
    | ['INBOX_MARKED_ALL_AS_READ', InboxOpenedData]
    | ['INBOX_OPENED', InboxOpenedData]
    | ['INBOX_ENTRY_NAVIGATED_TO_SOURCE', InboxEntryNavigateToSourceData]
    | ['INBOX_ENTRY_SOURCE_OPENED_IN_DRAWER', BaseInboxEntryData]
    | ['COMMAND_PALETTE_OPENED', CommandPaletteOpenedData]
    | ['COMMAND_PALETTE_USED', CommandPaletteUsedData]
    // OR-schedule
    | ['OR-SCHEDULE_FILTER_CHANGE', ORScheduleFilterChangeData]
    | ['OR-SCHEDULE_SIDEPANEL_BUTTON_CLICKED', DrawerData]
    | ['OR-DAY_NOTE_SAVED']
    | ['OR-DAY_NOTE_REMOVED']
    | ['PATIENT_SUGGESTIONS_DIALOG_OPENED', PatientSuggestionDialogOpenedClosedData]
    | ['PATIENT_SUGGESTIONS_DIALOG_CLOSED', PatientSuggestionDialogOpenedClosedData]
    | ['PATIENT_SUGGESTIONS_DIALOG_BACK_CLICKED', PatientSuggestionDialogBackClickedData]
    | ['PATIENT_SUGGESTIONS_DIALOG_BOOKING_FORM_OPENED', PatientSuggestionDialogBookingFormData]
    | ['PATIENT_SUGGESTIONS_DIALOG_UNSCHEDULING_FORM_OPENED', PatientSuggestionDialogUnschedulingFormData]
    | ['PATIENT_SUGGESTIONS_DIALOG_PATIENT_SUGGESTION_CLICKED', PatientSuggestionClickedData]
    | ['PATIENT_SUGGESTIONS_DIALOG_PREVIEW_SURGERY_REMOVED', PatientSuggestionDialogPreviewSurgeryRemovedData]
    | ['PATIENT_SUGGESTIONS_DIALOG_FILTER_CHANGE', PatientSuggestionFilterChangeData]
    | ['PATIENT_SUGGESTIONS_DIALOG_FILTER_CLEARED', undefined]
    | ['PATIENT_COUNT_HOVER_CARD_VIEWED', PatientCountHoverCardViewedData]
    // Patients
    | ['PATIENTS_SIDEPANEL_OPENED', PatientsSidepanelData]
    | ['AVAILABLE_TIME_SUGGESTION_CLICKED', AvailableTimeSuggestionData]
    | ['AVAILABLE_TIME_SUGGESTIONS_DIALOG_OPENED', AvailableTimeSuggestionDialogOpenedClosedData]
    | ['AVAILABLE_TIME_SUGGESTIONS_DIALOG_CLOSED', AvailableTimeSuggestionDialogOpenedClosedData]
    | ['AVAILABLE_TIME_SUGGESTIONS_DIALOG_BACK_CLICKED', AvailableTimeSuggestionDialogBackClickedData]
    | ['AVAILABLE_TIME_SUGGESTIONS_DIALOG_LOCATION_DAY_SELECTED', AvailableTimeSuggestionDialogLocationDayData]
    | ['AVAILABLE_TIME_SUGGESTIONS_DIALOG_BOOKING_FORM_OPENED', AvailableTimeSuggestionDialogBookingFormOpenedData]
    | ['AVAILABLE_TIME_SUGGESTIONS_DIALOG_FILTER_CHANGE', AvailableTimeSuggestionDialogFilterChangeData]
    | ['UNSCHEDULING_DIALOG_OPENED']
    | ['UNSCHEDULING_DIALOG_CLOSED']
    | ['SURGERY_BOOKMARK_ADDED']
    | ['SURGERY_BOOKMARK_REMOVED']
    // Capacity
    | ['OR-DAY_SEE_ALL_CLICKED', ORDayData]
    | ['BLOCK_SCHEDULE_BATCH_FORM', BlockScheduleBatchFormData]
    // Planning periods
    | ['PLANNING_PERIOD_ADDED', PlanningPeriodAddedData]
    | ['PLANNING_PERIOD_EDITED', PlanningPeriodEditedData]
    | ['PLANNING_PERIOD_PUBLICATION_STATUS_CHANGED', PlanningPeriodPublicationData]
    | ['PLANNING_PERIOD_REMOVED']
    | ['PLANNING_PERIOD_DESCRIPTION_ADDED', PlanningPeriodDescriptionData]
    | ['PLANNING_PERIOD_DESCRIPTION_EDITED', PlanningPeriodDescriptionData]
    | ['PLANNING_PERIOD_DESCRIPTION_REMOVED', PlanningPeriodDescriptionRemovedData]
    | ['SURGERY_PROGRAM_MODEL_RUN_STARTED', SurgeryProgramModelRunData]
    | ['SURGERY_PROGRAM_MODEL_RUN_CANCELLED', SurgeryProgramModelData]
    | ['PLANNING_PERIOD_VIEW_LINEGRAPH_CLICKED']
    // Staffing
    | ['LOCATION_NOTE_SAVED']
    | ['LOCATION_NOTE_REMOVED']
    | ['LOCATION_SURGEON_ADDED']
    | ['LOCATION_SURGEON_REMOVED']
    | ['SURGEON_NOTE_SAVED']
    | ['SURGEON_NOTE_REMOVED']
    | ['SURGEON_LOCATION_ADDED', SurgeonLocationData]
    | ['SURGEON_LOCATION_REMOVED', SurgeonLocationData]
    | ['SURGEON_ACTIVITY_ADDED', SurgeonActivityData]
    | ['SURGEON_ACTIVITY_REMOVED', SurgeonActivityData]
    | ['STAFFING_SIDEPANEL_OPENED']
    | ['STAFFING_SIDEPANEL_CLOSED']
    | ['STAFFING_WISH_EXPANDED', StaffingWishData]
    | ['STAFFING_WISH_CONDITIONS_HOVERED', StaffingWishData]
    | ['STAFFING_MODEL_RUN_STARTED', StaffingModelRunData]
    | ['STAFFING_MODEL_RUN_STOPPED', StaffingModelRunData]
    | ['STAFFING_MODEL_RUN_REJECTED', StaffingModelRunData]
    | ['STAFFING_MODEL_RUN_APPROVED', StaffingModelRunData]
    | ['STAFFING_TABLE_SETTING_CHANGED', StaffingTableSettingChangedData]
    // Retro
    | ['RETRO_OR_CLICKED', RetroORData]
    | ['RETRO_PLANNING-PERIOD_CHANGED', RetroPlanningPeriodData]
    // View options
    | ['CREATED_VIEW_OPTION_PROFILE', ViewOptionProfleData]
    | ['UPDATED_VIEW_OPTION_PROFILE', ViewOptionProfleData]
    | ['SELECTED_VIEW_OPTION_PROFILE', ViewOptionProfleData]
    | ['DELETED_VIEW_OPTION_PROFILE']
    | ['VIEW_OPTION_MENU_OPENED']
    | ['VIEW_OPTION_VISIBILITY_TOGGLED', VisibilityOptionToggledData]
    | ['VIEW_OPTION_LAYOUT_CHANGED', LayoutOptionChangedData]
    // Planning note
    | ['PLANNING_NOTE_HISTORY_OPENED', PlanningNoteHistoryOpenedData]
    | ['PLANNING_NOTE_MIGRATION', PlanningNoteMigrationData]
    | ['PLANNING_NOTE_SAVED', PlanningNoteSavedData]
    // Hospital configuration
    | ['HOSPITAL_CONFIGURATION_PRACTITIONER_CREATED', HospitalConfigurationPractitionerSavedData]
    | ['HOSPITAL_CONFIGURATION_PRACTITIONER_EDITED', HospitalConfigurationPractitionerEditedData]
    | ['HOSPITAL_CONFIGURATION_PRACTITIONER_ORDER_SAVED', HospitalConfigurationPractitionerOrderSavedData]
    | ['HOSPITAL_CONFIGURATION_ROOM_CREATED', HospitalConfigurationRoomSavedData]
    | ['HOSPITAL_CONFIGURATION_ROOM_EDITED', HospitalConfigurationRoomEditedData]
    // Release notes
    | ['RELEASE_NOTE_DIALOG_OPEN', ReleaseNoteData]
    | ['RELEASE_NOTE_MARK_SEEN', ReleaseNoteData]

export type UserEventKey = UserEvent[0]
export type UserEventDataMap = {
    [E in UserEvent as E[0]]: E extends [infer _Key, infer Data] ? (Data extends undefined ? undefined : Data) : never
}

export const userEventNames: Record<UserEventKey, string> = {
    // Across pages
    VERSION_UPDATE_TOAST_SHOWN: 'Version Update Toast Shown',
    VERSION_UPDATE_TOAST_ACCEPTED: 'Version Update Toast Accepted',
    VERSION_UPDATE_TOAST_DISMISSED: 'Version Update Toast Dismissed',
    LAZY_LOAD_ERROR_FALLBACK_SHOWN: 'Lazy Load Error Fallback Shown',
    'OR-DAY_BLOCK_SCHEDULE_ADDED': 'OR-Day Block Schedule Added',
    'OR-DAY_BLOCK_SCHEDULE_UPDATED': 'OR-Day Block Schedule Updated',
    'OR-DAY_BLOCK_SCHEDULE_REMOVED': 'OR-Day Block Schedule Removed',
    LOCATION_SERVICE_OVERRIDE_ADDED: 'Location Service Override Added',
    LOCATION_SERVICE_OVERRIDE_UPDATED: 'Location Service Override Updated',
    LOCATION_SERVICE_OVERRIDE_REMOVED: 'Location Service Override Removed',
    LOCATION_SERVICE_OVERRIDE_FORM_OPENED: 'Location Service Override Form Opened',
    LOCATION_SERVICE_OVERRIDE_FORM_CLOSED: 'Location Service Override Form Closed',
    SURGERY_METADATA_SAVED: 'Surgery Metadata Saved',
    NATIONAL_ID_COPIED: 'National ID Copied',
    DEPARTMENT_SELECTED: 'Department Selected',
    SPECIALTIES_SELECTED: 'Specialties Selected',
    SURGERY_BOOKED: 'Surgery Booked',
    SURGERY_BOOKING_FAILED: 'Surgery Booking Failed',
    SURGERY_UNSCHEDULED: 'Surgery Unscheduled',
    DRAWER_TAB_SELECTED: 'Drawer Tab Selected',
    'OR-DAY_CLICKED': 'OR-Day Clicked',
    'OR-DAY_COMMENT_ADDED': 'OR-Day Comment Added',
    'OR-DAY_COMMENT_DELETED': 'OR-Day Comment Deleted',
    COMMENT_THREAD_CREATED: 'Comment Thread Created',
    COMMENT_MESSAGE_CREATED: 'Comment Message Created',
    COMMENT_REACTION_ADDED: 'Comment Reaction Added',
    COMMENT_REACTION_REMOVED: 'Comment Reaction Removed',
    COMMENT_THREAD_RESOLVED: 'Comment Thread Resolved',
    COMMENT_THREAD_UNRESOLVED: 'Comment Thread Unresolved',
    COMMENT_THREAD_ARCHIVED: 'Comment Thread Archived',
    COMMENT_THREAD_PINNED: 'Comment Thread Pinned',
    COMMENT_THREAD_UNPINNED: 'Comment Thread Unpinned',
    COMMENT_CONTENT_EDITED: 'Comment Edited',
    INBOX_ENTRY_MARKED_AS_READ: 'Inbox Entry Marked as Read',
    INBOX_MARKED_ALL_AS_READ: 'Inbox Marked All as Read',
    INBOX_OPENED: 'Inbox Opened',
    INBOX_ENTRY_NAVIGATED_TO_SOURCE: 'Inbox Entry Navigated to Source',
    INBOX_ENTRY_SOURCE_OPENED_IN_DRAWER: 'Inbox Entry Source Opened in Drawer',
    COMMAND_PALETTE_OPENED: 'Command Palette Opened',
    COMMAND_PALETTE_USED: 'Command Palette Used',
    // OR-schedule
    'OR-SCHEDULE_FILTER_CHANGE': 'OR-Schedule Filter Change',
    'OR-SCHEDULE_SIDEPANEL_BUTTON_CLICKED': 'OR-Schedule Sidepanel Button Clicked',
    'OR-DAY_NOTE_SAVED': 'OR-Day Note Saved',
    'OR-DAY_NOTE_REMOVED': 'OR-Day Note Removed',
    PATIENT_SUGGESTIONS_DIALOG_OPENED: 'Patient Suggestions Dialog Opened',
    PATIENT_SUGGESTIONS_DIALOG_CLOSED: 'Patient Suggestions Dialog Closed',
    PATIENT_SUGGESTIONS_DIALOG_BACK_CLICKED: 'Patient Suggestions Dialog Back Clicked',
    PATIENT_SUGGESTIONS_DIALOG_BOOKING_FORM_OPENED: 'Patient Suggestions Dialog Booking Form Opened',
    PATIENT_SUGGESTIONS_DIALOG_UNSCHEDULING_FORM_OPENED: 'Patient Suggestions Dialog Unscheduling Form Opened',
    PATIENT_SUGGESTIONS_DIALOG_PATIENT_SUGGESTION_CLICKED: 'Patient Suggestions Dialog Patient Suggestion Clicked',
    PATIENT_SUGGESTIONS_DIALOG_PREVIEW_SURGERY_REMOVED: 'Patient Suggestions Dialog Preview Surgery Removed',
    PATIENT_SUGGESTIONS_DIALOG_FILTER_CHANGE: 'Patient Suggestions Dialog Filter Change',
    PATIENT_SUGGESTIONS_DIALOG_FILTER_CLEARED: 'Patient Suggestions Dialog Filter Cleared',
    PATIENT_COUNT_HOVER_CARD_VIEWED: 'Patient Count Hover Card Viewed',
    // Patients
    PATIENTS_SIDEPANEL_OPENED: 'Patients Sidepanel Opened',
    AVAILABLE_TIME_SUGGESTION_CLICKED: 'Available Time Suggestion Clicked',
    AVAILABLE_TIME_SUGGESTIONS_DIALOG_OPENED: 'Available Time Suggestions Dialog Opened',
    AVAILABLE_TIME_SUGGESTIONS_DIALOG_CLOSED: 'Available Time Suggestions Dialog Closed',
    AVAILABLE_TIME_SUGGESTIONS_DIALOG_BACK_CLICKED: 'Available Time Suggestions Dialog Back Clicked',
    AVAILABLE_TIME_SUGGESTIONS_DIALOG_LOCATION_DAY_SELECTED: 'Available Time Suggestions Dialog Location Day Selected',
    AVAILABLE_TIME_SUGGESTIONS_DIALOG_BOOKING_FORM_OPENED: 'Available Time Suggestions Dialog Opened Booking Form',
    AVAILABLE_TIME_SUGGESTIONS_DIALOG_FILTER_CHANGE: 'Available Time Suggestions Dialog Filter Change',
    UNSCHEDULING_DIALOG_OPENED: 'Unscheduling Dialog Opened',
    UNSCHEDULING_DIALOG_CLOSED: 'Unscheduling Dialog Closed',
    SURGERY_BOOKMARK_ADDED: 'Surgery Bookmark Added',
    SURGERY_BOOKMARK_REMOVED: 'Surgery Bookmark Removed',
    // Capacity
    'OR-DAY_SEE_ALL_CLICKED': 'OR-Day See All Clicked',
    BLOCK_SCHEDULE_BATCH_FORM: 'Block Schedule Batch Form',
    // Planning periods
    PLANNING_PERIOD_ADDED: 'Planning Period Added',
    PLANNING_PERIOD_EDITED: 'Planning Period Edited',
    PLANNING_PERIOD_PUBLICATION_STATUS_CHANGED: 'Planning Period Publication Status Changed',
    PLANNING_PERIOD_REMOVED: 'Planning Period Removed',
    PLANNING_PERIOD_DESCRIPTION_ADDED: 'Planning Period Description Added',
    PLANNING_PERIOD_DESCRIPTION_EDITED: 'Planning Period Description Edited',
    PLANNING_PERIOD_DESCRIPTION_REMOVED: 'Planning Period Description Removed',
    PLANNING_PERIOD_VIEW_LINEGRAPH_CLICKED: 'Planning Period View Linegraph Clicked',
    SURGERY_PROGRAM_MODEL_RUN_STARTED: 'Surgery Program Model Run Started',
    SURGERY_PROGRAM_MODEL_RUN_CANCELLED: 'Surgery Program Model Run Cancelled by User',
    // Staffing
    LOCATION_NOTE_SAVED: 'Location Note Saved',
    LOCATION_NOTE_REMOVED: 'Location Note Removed',
    LOCATION_SURGEON_ADDED: 'Location Surgeon Added',
    LOCATION_SURGEON_REMOVED: 'Location Surgeon Removed',
    SURGEON_NOTE_SAVED: 'Surgeon Note Saved',
    SURGEON_NOTE_REMOVED: 'Surgeon Note Removed',
    SURGEON_LOCATION_ADDED: 'Surgeon Location Added',
    SURGEON_LOCATION_REMOVED: 'Surgeon Location Removed',
    SURGEON_ACTIVITY_ADDED: 'Surgeon Activity Added',
    SURGEON_ACTIVITY_REMOVED: 'Surgeon Activity Removed',
    STAFFING_SIDEPANEL_OPENED: 'Staffing Sidepanel Opened',
    STAFFING_SIDEPANEL_CLOSED: 'Staffing Sidepanel Closed',
    STAFFING_WISH_EXPANDED: 'Staffing Wish Expanded',
    STAFFING_WISH_CONDITIONS_HOVERED: 'Staffing Wish Conditions Hovered',
    STAFFING_MODEL_RUN_STARTED: 'Staffing Model Run Started',
    STAFFING_MODEL_RUN_STOPPED: 'Staffing Model Run Stopped',
    STAFFING_MODEL_RUN_REJECTED: 'Staffing Model Run Rejected',
    STAFFING_MODEL_RUN_APPROVED: 'Staffing Model Run Approved',
    STAFFING_TABLE_SETTING_CHANGED: 'Staffing Table Setting Changed',
    // Retro
    RETRO_OR_CLICKED: 'Retro OR Change',
    'RETRO_PLANNING-PERIOD_CHANGED': 'Retro Planning Period Changed',
    // View options
    CREATED_VIEW_OPTION_PROFILE: 'Created view option profile',
    UPDATED_VIEW_OPTION_PROFILE: 'Updated view option profile',
    SELECTED_VIEW_OPTION_PROFILE: 'Selected view option profile',
    DELETED_VIEW_OPTION_PROFILE: 'Deleted view option profile',
    VIEW_OPTION_MENU_OPENED: 'View option menu opened',
    VIEW_OPTION_VISIBILITY_TOGGLED: 'View option visibility toggled',
    VIEW_OPTION_LAYOUT_CHANGED: 'View option layout changed',
    // Planning note
    PLANNING_NOTE_HISTORY_OPENED: 'Planning note history opened',
    PLANNING_NOTE_MIGRATION: 'Planning note migration',
    PLANNING_NOTE_SAVED: 'Planning Note Saved',
    // Hospital configuration
    HOSPITAL_CONFIGURATION_PRACTITIONER_CREATED: 'Hospital Configuration Practitioner Created',
    HOSPITAL_CONFIGURATION_PRACTITIONER_EDITED: 'Hospital Configuration Practitioner Edited',
    HOSPITAL_CONFIGURATION_PRACTITIONER_ORDER_SAVED: 'Hospital Configuration Practitioner Order Saved',
    HOSPITAL_CONFIGURATION_ROOM_CREATED: 'Hospital Configuration Room Created',
    HOSPITAL_CONFIGURATION_ROOM_EDITED: 'Hospital Configuration Room Edited',
    // Release notes
    RELEASE_NOTE_DIALOG_OPEN: 'Release note dialog opened',
    RELEASE_NOTE_MARK_SEEN: 'Release notes marked as seen',
}
